import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import React from 'react';

import {CoordinateDisplay} from 'app/components/CoordinateDisplay';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {getAreaUnit} from 'app/modules/Remote/Organization';
import {useUserInfo} from 'app/providers/AuthProvider';
import {StateApiNote} from 'app/stores/NotesStore';
import colors from 'app/styles/colors.json';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as noteUtils from 'app/utils/noteUtils';

import cs from './NoteCard.module.scss';

const MAX_COORDINATES_FOR_AREA_CALCULATION = 1000;

export const SceneLocationPopover: React.FunctionComponent<
  React.PropsWithChildren<{
    note: StateApiNote;
    featureGeometry?: geojson.Geometry;
    projectOverlays?: ApiFeatureCollection[];
  }>
> = ({note, featureGeometry, projectOverlays}) => {
  const [organization] = useUserInfo();
  const hasGeometry = !!note.geometry;
  const hasLayer = noteUtils.imageRefsArentBasemap(note.imageRefs);

  const activeOverlayNames = React.useMemo(() => {
    if (!note.overlayFeatureCollectionIds?.length || !projectOverlays?.length) return [];
    const idSet = new Set(note.overlayFeatureCollectionIds);
    return projectOverlays
      .filter((o) => idSet.has(o.id))
      .map((o) => o.name)
      .sort((a, b) => featureUtils.alphanumericSort(a, b));
  }, [note.overlayFeatureCollectionIds, projectOverlays]);

  const position = geoJsonUtils.makeCoordinateParts(note.geometry);

  const noteGeometry = note.geometry;

  const geometryCoordinatesCount = (geometry?: geojson.Geometry) => {
    switch (geometry?.type) {
      case 'Polygon':
        return geometry.coordinates[0].length;
      case 'MultiPolygon':
        return geometry.coordinates.reduce((sum, polygon) => sum + polygon[0].length, 0);
      default:
        return 0;
    }
  };

  const allowAreaCalculation =
    !!noteGeometry &&
    (noteGeometry.type === 'Polygon' || noteGeometry.type === 'MultiPolygon') &&
    geometryCoordinatesCount(noteGeometry) <= MAX_COORDINATES_FOR_AREA_CALCULATION;

  const area = allowAreaCalculation
    ? geoJsonUtils.makeAreaDisplayString(noteGeometry, getAreaUnit(organization!))
    : null;

  const allowPercentOfPropertyCalculation =
    allowAreaCalculation &&
    !!featureGeometry &&
    (featureGeometry.type === 'Polygon' || featureGeometry.type === 'MultiPolygon') &&
    geometryCoordinatesCount(featureGeometry) <= MAX_COORDINATES_FOR_AREA_CALCULATION;

  const percentOfProperty = allowPercentOfPropertyCalculation
    ? (geoJsonUtils.UTMArea(noteGeometry) / geoJsonUtils.UTMArea(featureGeometry!)) * 100
    : null;

  return (
    <B.Popover
      minimal
      interactionKind={B.PopoverInteractionKind.HOVER}
      modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
      position={'bottom'}
      content={
        <div className={cs.popoverContent}>
          {hasGeometry && (
            <div>
              <div className={cs.detailsLabel}>Location</div>
              {area && (
                <div>
                  {area}
                  {percentOfProperty && ` (${percentOfProperty.toFixed(2)}% of property)`}
                </div>
              )}
              {position && (
                <div>
                  {note.geometry!.type === 'Point' ? 'Point at' : 'Centered at'}{' '}
                  <CoordinateDisplay geometry={note.geometry} />
                </div>
              )}
            </div>
          )}
          {hasLayer && (
            <div>
              <div className={cs.detailsLabel}>
                {note.imageRefs.length === 1 ? 'Scene' : 'Scenes'}
              </div>
              <div>
                {note.imageRefs.map(({cursor, layerKey}, i) => {
                  return (
                    <div key={i}>
                      {`${layerUtils.getLayer(layerKey).display} on ${imageryUtils.formatDate(
                        cursor || '',
                        layerKey
                      )}`}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!!activeOverlayNames.length && (
            <div>
              <div className={cs.detailsLabel}>
                {activeOverlayNames.length === 1 ? 'Overlay' : 'Overlays'}
              </div>
              <div>
                {activeOverlayNames.map((name) => (
                  <div key={name}>{name}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    >
      <B.AnchorButton
        small
        minimal
        icon={
          <B.Icon
            icon="area-of-interest"
            color={colors.gray}
            size={14}
            onClick={(e) => e.stopPropagation()}
          />
        }
      />
    </B.Popover>
  );
};
